import PropTypes from "prop-types";

import Money from "../../../components/Money";
import { TransactionList } from "../components/TransactionList";
import { playerStatus, owedAmount, unsettledBuyIns } from "./playerGroups";

/**
 * Full-width player tile for the mobile ledger. Collapsed it shows the
 * player, a status line, their net, and the two primary actions
 * (+ buy-in / ↗ cash-out). Tapping the body expands the tile in place
 * to reveal the player's transaction history (reusing the shared
 * `TransactionList`) plus full-width per-player CTAs.
 */
export function PlayerTile({
    gameId,
    player,
    expanded,
    onToggle,
    onBuyIn,
    onCashOut,
}) {
    const totals = player.totals ?? {};
    const transactions = player.transactions ?? [];
    const net = totals.net ?? 0;
    const netClass = net > 0 ? "is-up" : net < 0 ? "is-down" : "is-zero";

    const st = playerStatus(player);
    const owing = st.tone === "owing";
    const buyInCount = transactions.filter((t) => t.type === "BUY_IN").length;
    const primary =
        transactions.length === 0
            ? "No transactions yet"
            : `${buyInCount} buy-in${buyInCount === 1 ? "" : "s"}`;
    // Amount-free status: the figures column already shows the owe/owed amount,
    // so the line just carries the settlement state (cashed out / unsettled
    // count / all settled). The tone class still tints it by direction.
    const secondary =
        transactions.length === 0
            ? null
            : st.cashedOut
              ? "cashed out"
              : owing
                ? `${unsettledBuyIns(player).length} unsettled`
                : "all settled";

    const stop = (fn) => (e) => {
        e.stopPropagation();
        fn?.();
    };

    return (
        <div className={`mlg-tile is-${st.tone}${st.cashedOut ? " is-cashed-out" : ""}${expanded ? " is-expanded" : ""}`}>
            <button
                type="button"
                className="mlg-tile-main"
                onClick={onToggle}
                aria-expanded={expanded}
            >
                <span className="mlg-tile-av" aria-hidden="true">
                    {initials(player.name)}
                </span>
                <span className="mlg-tile-info">
                    <span className="mlg-tile-name">{player.name}</span>
                    <span className="mlg-tile-meta">
                        <StatusPip kind={st.tone} />
                        {primary}
                    </span>
                    {secondary && (
                        <span className={`mlg-tile-meta2 is-${st.tone}`}>
                            {secondary}
                        </span>
                    )}
                </span>
                <span className="mlg-tile-figures">
                    <span className={`mlg-tile-net ${netClass}`}>
                        <Money amount={net} signed={net !== 0} />
                    </span>
                    <span className="mlg-tile-sub">
                        {owing ? (
                            <span className="owes">
                                owes <Money amount={owedAmount(player)} />
                            </span>
                        ) : st.owedOut > 0 ? (
                            <span className="owed">
                                owed <Money amount={st.owedOut} />
                            </span>
                        ) : (
                            <>
                                <Money amount={totals.buyIn ?? 0} /> in
                            </>
                        )}
                    </span>
                </span>
            </button>

            <div className="mlg-tile-acts">
                <button
                    type="button"
                    className="mlg-act add"
                    aria-label={`Add buy-in for ${player.name}`}
                    onClick={stop(onBuyIn)}
                >
                    +
                </button>
                <button
                    type="button"
                    className="mlg-act cashout"
                    aria-label={`Cash out ${player.name}`}
                    onClick={stop(onCashOut)}
                >
                    ↗
                </button>
            </div>

            {expanded && (
                <div className="mlg-tile-detail">
                    <TransactionList
                        gameId={gameId}
                        playerId={player.id}
                        transactions={transactions}
                    />
                    <div className="mlg-tile-detail-acts">
                        <button
                            type="button"
                            className="mlg-btn primary"
                            onClick={stop(onBuyIn)}
                        >
                            + Buy-in
                        </button>
                        <button
                            type="button"
                            className="mlg-btn brick-outline"
                            onClick={stop(onCashOut)}
                        >
                            ↗ Cash out
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const PIP_CLASS = {
    owing: "is-warn",
    owed: "is-owed",
    settled: "is-up",
    active: "is-up",
    empty: "is-neutral",
};

const StatusPip = ({ kind }) => (
    <span className={`mlg-pip ${PIP_CLASS[kind] ?? "is-neutral"}`} aria-hidden="true" />
);

function initials(name) {
    if (!name) return "·";
    const parts = name.trim().split(/\s+/);
    return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
}

PlayerTile.propTypes = {
    gameId: PropTypes.string,
    player: PropTypes.object.isRequired,
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
    onBuyIn: PropTypes.func,
    onCashOut: PropTypes.func,
};
